import React, { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, MapPin, Sparkles, Building2, GraduationCap,
  CircleCheck, Briefcase, Workflow, LineChart, Package, ScanLine, FileCheck,
  UserCheck, ClipboardList, MessageSquare, IndianRupee, CheckCircle2,
  BarChart3, Send, Star, Menu, X, Clock,
} from "lucide-react";
import { Logo } from "../components/shared/Logo";
import { ThemeToggle } from "../components/shared/ThemeToggle";
import LandingHero2D from "../components/landing/LandingHero2D";

const LandingHero3D = lazy(() => import("../components/landing/LandingHero3D"));

/** Detect small screens + reduced-motion; use lightweight fallback. */
function useIsLightweight() {
  const [light, setLight] = useState(true);
  useEffect(() => {
    const mm = window.matchMedia("(min-width: 1024px)");
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setLight(!mm.matches || rm.matches);
    update();
    mm.addEventListener?.("change", update);
    rm.addEventListener?.("change", update);
    return () => {
      mm.removeEventListener?.("change", update);
      rm.removeEventListener?.("change", update);
    };
  }, []);
  return light;
}

/* -------- header -------- */
const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="landing-capsule-nav" className="sticky top-3 z-40 px-3 md:px-6">
      <div className="capsule-nav max-w-6xl mx-auto pl-2 pr-2 md:pl-3 py-2 flex items-center gap-3">
        <Logo/>
        <nav className="hidden lg:flex items-center gap-5 ml-4 text-sm" style={{ color: "var(--text-muted)" }}>
          <a href="#how" className="hover:opacity-100 opacity-80 transition-opacity">How it works</a>
          <a href="#students" className="hover:opacity-100 opacity-80 transition-opacity">For students</a>
          <a href="#companies" className="hover:opacity-100 opacity-80 transition-opacity">For companies</a>
          <a href="#admin" className="hover:opacity-100 opacity-80 transition-opacity">Admin</a>
          <a href="#trust" className="hover:opacity-100 opacity-80 transition-opacity">Trust</a>
          <a href="#roadmap" className="hover:opacity-100 opacity-80 transition-opacity">Roadmap</a>
        </nav>
        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <ThemeToggle/>
          <Link to="/auth" data-testid="nav-signin-link" className="hidden sm:inline text-sm px-3 py-1.5 transition-colors" style={{ color: "var(--text-muted)" }}>Sign in</Link>
          <Link to="/auth" data-testid="nav-signup-btn" className="btn-primary text-sm px-3 md:px-4 py-2 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="sm:hidden">Join</span><span className="hidden sm:inline">Get started</span> <ArrowRight size={14} className="hidden sm:inline"/>
          </Link>
          <button data-testid="landing-menu-btn" className="lg:hidden p-2 rounded-full hover:bg-white/5" onClick={() => setOpen(v => !v)} aria-label="Menu" style={{ color: "var(--text)" }}>
            {open ? <X size={18}/> : <Menu size={18}/>}
          </button>
        </div>
      </div>
      {open && (
        <div data-testid="landing-mobile-menu" className="lg:hidden capsule-nav max-w-6xl mx-auto mt-2 !rounded-2xl">
          <div className="px-5 py-4 grid gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
            {[["#how","How it works"],["#students","For students"],["#companies","For companies"],["#admin","Admin"],["#trust","Trust"],["#roadmap","Roadmap"]].map(([h,l]) => (
              <a key={h} href={h} onClick={() => setOpen(false)} className="py-1.5">{l}</a>
            ))}
            <Link to="/auth" className="py-1.5">Sign in</Link>
          </div>
        </div>
      )}
    </header>
  );
};

/* -------- reusable bits -------- */
const Eyebrow = ({ children }) => (
  <div className="eyebrow inline-flex items-center gap-2" style={{ color: "var(--brand)" }}>{children}</div>
);

const SectionTitle = ({ eyebrow, title, sub }) => (
  <div className="max-w-2xl">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="mt-3 font-display text-2xl sm:text-3xl md:text-4xl font-medium" style={{ color: "var(--text)" }}>{title}</h2>
    {sub && <p className="mt-3 text-sm md:text-base" style={{ color: "var(--text-muted)" }}>{sub}</p>}
  </div>
);

const Feature = ({ icon: Icon, tag, title, desc }) => (
  <div className="card-surface card-hover p-5 md:p-6 relative">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-9 h-9 rounded-md flex items-center justify-center"
           style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)" }}>
        <Icon size={16} style={{ color: "var(--brand)" }}/>
      </div>
      <span className="eyebrow">{tag}</span>
    </div>
    <h3 className="font-display text-base md:text-lg mb-1.5" style={{ color: "var(--text)" }}>{title}</h3>
    <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>{desc}</p>
  </div>
);

const FlowStep = ({ n, icon: Icon, label, desc }) => (
  <div className="relative card-surface p-4 md:p-5 flex-1 min-w-[180px]">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-md flex items-center justify-center"
           style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)" }}>
        <Icon size={16} style={{ color: "var(--brand)" }}/>
      </div>
      <div className="text-xs font-mono" style={{ color: "var(--text-dim)" }}>STEP {String(n).padStart(2, "0")}</div>
    </div>
    <div className="mt-3 text-sm font-medium" style={{ color: "var(--text)" }}>{label}</div>
    <div className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>{desc}</div>
  </div>
);

const Tag = ({ label, color = "var(--brand)" }) => (
  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border"
        style={{ borderColor: color + "55", color, background: color + "12" }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }}/>{label}
  </span>
);

const ChipList = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map(x => (
      <span key={x} className="text-[11px] px-2 py-1 rounded-full border"
            style={{ borderColor: "var(--border-strong)", color: "var(--text-muted)" }}>{x}</span>
    ))}
  </div>
);

/* -------- page -------- */
export default function Landing() {
  const navigate = useNavigate();
  const lightweight = useIsLightweight();

  return (
    <div style={{ background: "var(--bg)", color: "var(--text)" }}>
      <Nav/>

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b" style={{ borderColor: "var(--border)" }}>
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none"/>
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "radial-gradient(1200px 600px at 70% 20%, rgba(59,130,246,0.14), transparent 60%)" }}/>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-14 md:pt-20 pb-16 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 xl:col-span-6">
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full"
                   style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)", color: "var(--brand)" }}>
                <Sparkles size={12}/> India's verified student-to-business marketplace
              </div>
              <h1 className="mt-6 font-display font-medium tracking-tight leading-[1.02] text-[clamp(2.4rem,6vw,4.5rem)]"
                  style={{ color: "var(--text)" }}>
                <span className="block">FIND WORK.</span>
                <span className="block">BUILD EXPERIENCE.</span>
                <span className="block gradient-text">GET PAID.</span>
              </h1>
              <p className="mt-5 md:mt-6 text-base md:text-lg max-w-xl" style={{ color: "var(--text-muted)" }}>
                A trusted platform connecting student talent with real business opportunities — verified profiles, admin-approved tasks, escrowed payments.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button onClick={() => navigate("/auth")} data-testid="hero-find-tasks-btn"
                        className="btn-primary px-5 py-3 rounded-full inline-flex items-center gap-2 text-sm font-medium">
                  <Briefcase size={15}/> Find Tasks
                </button>
                <button onClick={() => navigate("/auth")} data-testid="hero-post-task-btn"
                        className="btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2 text-sm font-medium">
                  <Building2 size={15}/> Post a Task
                </button>
              </div>

              <div className="mt-8 md:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
                {[
                  { v: "12.4k", l: "Verified students" },
                  { v: "1,284", l: "Companies" },
                  { v: "₹2.8Cr", l: "Paid to date" },
                  { v: "4.87",  l: "Avg. rating" },
                ].map(s => (
                  <div key={s.l} className="border-l pl-3" style={{ borderColor: "var(--border-strong)" }}>
                    <div className="font-display text-xl md:text-2xl tabular-nums" style={{ color: "var(--text)" }}>{s.v}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-dim)" }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 3D visual (or SVG fallback on mobile / reduced-motion) */}
            <div className="lg:col-span-6 xl:col-span-6">
              <div className="relative w-full h-[320px] sm:h-[400px] md:h-[460px] lg:h-[520px]">
                {lightweight ? <LandingHero2D/> : (
                  <Suspense fallback={<LandingHero2D/>}>
                    <LandingHero3D/>
                  </Suspense>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how" className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <SectionTitle
          eyebrow={<><Workflow size={12}/> How it works</>}
          title="A structured workflow from posting to payout."
          sub="Every task moves through verified stages so students and companies always know what's next."
        />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <FlowStep n={1} icon={ShieldCheck}   label="VERIFY"    desc="KYC + GST verification before any task or apply."/>
          <FlowStep n={2} icon={UserCheck}     label="MATCH"     desc="Skills, location and reputation used to shortlist."/>
          <FlowStep n={3} icon={ClipboardList} label="ALLOCATE"  desc="Admin allocates the best fit; both sides get notified."/>
          <FlowStep n={4} icon={MessageSquare} label="WORK"      desc="Workroom, OTP check-in, deliverables & timeline."/>
          <FlowStep n={5} icon={IndianRupee}   label="GET PAID"  desc="Escrowed budget releases when work is approved."/>
        </div>
      </section>

      {/* ============ FOR STUDENTS ============ */}
      <section id="students" className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-8 md:gap-12">
            <div className="lg:col-span-5">
              <SectionTitle
                eyebrow={<><GraduationCap size={12}/> For students</>}
                title="Real experience, real pay — nearby."
                sub="Apply to tasks that match your skills, get allocated by Admin, and build a verifiable reputation."
              />
              <div className="mt-6 flex flex-wrap gap-2">
                <Tag label="KYC verified"/>
                <Tag label="Match score" color="#22D3EE"/>
                <Tag label="Escrowed payout" color="#10B981"/>
                <Tag label="Location-aware" color="#F59E0B"/>
              </div>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              <Feature icon={ShieldCheck}   tag="Profile"       title="Verified profile"       desc="Aadhaar-backed KYC, education and skills that unlock task access."/>
              <Feature icon={MapPin}        tag="Nearby"        title="Location-based tasks"   desc="Service radius filtering — only see tasks you can actually deliver."/>
              <Feature icon={ClipboardList} tag="Applications"  title="Track applications"     desc="One-tap apply. Live status. Instant notification when allocated."/>
              <Feature icon={MessageSquare} tag="Workroom"      title="Structured workroom"    desc="Timeline, chat, files and OTP check-ins per engagement."/>
              <Feature icon={IndianRupee}   tag="Earnings"      title="Reliable payout"        desc="Approved tasks trigger escrow release; earnings dashboard tracks history."/>
              <Feature icon={Star}          tag="Reputation"    title="Ratings & trust score"  desc="Every completed task grows a portable, verifiable reputation."/>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOR COMPANIES ============ */}
      <section id="companies" className="border-t" style={{ borderColor: "var(--border)" }}
               data-testid="section-companies">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-8 md:gap-12">
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 order-2 lg:order-1">
              <Feature icon={FileCheck}     tag="Verification" title="GST-verified company"  desc="GSTIN, legal name and address verified before you can publish."/>
              <Feature icon={ClipboardList} tag="Task"         title="Post & scope"          desc="Structured task creation — scope, budget, deadline, skills."/>
              <Feature icon={UserCheck}     tag="Matching"     title="Pre-screened talent"   desc="Admin allocates the best-matched, KYC-verified student to you."/>
              <Feature icon={MessageSquare} tag="Workroom"     title="Track execution"       desc="Chat, files, milestones and OTP check-ins in one workroom."/>
              <Feature icon={CheckCircle2}  tag="Delivery"     title="Review & approve"      desc="Approve deliverables to release escrow, or request revisions."/>
              <Feature icon={IndianRupee}   tag="Payments"     title="Escrow-first payouts"  desc="Funds held until approval; no back-office chasing."/>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <SectionTitle
                eyebrow={<><Building2 size={12}/> For companies</>}
                title="Pre-screened student talent, on demand."
                sub="Post one-off tasks and get KYC-verified students allocated by our admin team — with escrow and audit trail."
              />
              <div className="mt-6 flex flex-wrap gap-2">
                <Tag label="GST verified"/>
                <Tag label="Admin-approved tasks" color="#22D3EE"/>
                <Tag label="Escrow" color="#10B981"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ADMIN CONTROL CENTER ============ */}
      <section id="admin" className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <SectionTitle
            eyebrow={<><Send size={12}/> Admin control center</>}
            title="A central operator on every engagement."
            sub="Assisto4U's admin verifies, allocates and monitors every task — turning a marketplace into a supervised workflow."
          />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { i: ShieldCheck, l: "VERIFY",    d: "KYC + GST" },
              { i: CheckCircle2, l: "APPROVE",  d: "Task listings" },
              { i: UserCheck,   l: "MATCH",     d: "Student ↔ task" },
              { i: ClipboardList, l: "ALLOCATE",d: "Assign & notify" },
              { i: BarChart3,   l: "MONITOR",   d: "Analytics & disputes" },
            ].map((s, i) => (
              <FlowStep key={s.l} n={i + 1} icon={s.i} label={s.l} desc={s.d}/>
            ))}
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <Feature icon={Send}          tag="Direct Work"  title="Admin ↔ student channel" desc="Only admin can initiate a direct workroom to send tasks, proposals or assignments."/>
            <Feature icon={LineChart}     tag="Analytics"    title="Platform pulse"          desc="Totals, trends, city-wise activity and CSV export at a glance."/>
            <Feature icon={ShieldCheck}   tag="Audit"        title="Full audit trail"        desc="Every decision, message and status change is logged and searchable."/>
          </div>
        </div>
      </section>

      {/* ============ TRUST ============ */}
      <section id="trust" className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionTitle
                eyebrow={<><ShieldCheck size={12}/> Built for trust</>}
                title="Every profile earns the right to work here."
                sub="Verified end-to-end so companies get real students and students get real work."
              />
              <ul className="mt-8 space-y-3">
                {[
                  ["Student KYC (Aadhaar-backed identity)", "#10B981"],
                  ["Company KYC + GSTIN legal-name match",  "#10B981"],
                  ["Location verification via service radius", "#3B82F6"],
                  ["Ratings, reputation and completion rate", "#3B82F6"],
                  ["Secure workrooms with OTP check-in",     "#22D3EE"],
                  ["Full audit trail on every engagement",    "#22D3EE"],
                ].map(([t, c]) => (
                  <li key={t} className="flex items-start gap-3 text-sm" style={{ color: "var(--text)" }}>
                    <CircleCheck size={16} style={{ color: c }} className="mt-0.5 flex-shrink-0"/>{t}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs" style={{ color: "var(--text-dim)" }}>
                Sensitive documents are never public — all KYC documents are stored securely and reviewed by admin only.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {[
                { l: "Verified students", v: "12,483" },
                { l: "Verified companies", v: "1,284" },
                { l: "Tasks completed", v: "9,721" },
                { l: "Avg. task rating", v: "4.87 / 5" },
                { l: "Payment success", v: "99.6%" },
                { l: "Cities live", v: "42" },
              ].map(s => (
                <div key={s.l} className="p-4 md:p-5 rounded-md border"
                     style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                  <div className="font-display text-xl md:text-2xl tabular-nums" style={{ color: "var(--text)" }}>{s.v}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ ROADMAP: STOCK AUDIT ============ */}
      <section id="roadmap" className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <SectionTitle
            eyebrow={<><Clock size={12}/> In the works</>}
            title="What's coming next to Assisto4U."
            sub="Purpose-built workflows for professional service categories — designed with the same verification-first architecture."
          />

          <div className="mt-10 grid lg:grid-cols-2 gap-4 md:gap-6">
            {/* Stock Audit */}
            <div className="card-surface p-6 md:p-8 relative overflow-hidden">
              <div className="absolute inset-0 opacity-40 pointer-events-none grid-bg-fine"/>
              <div className="relative flex items-center justify-between mb-4">
                <Eyebrow><Package size={12}/> Stock Audit</Eyebrow>
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border"
                      style={{ borderColor: "rgba(245,158,11,0.35)", color: "#F59E0B", background: "rgba(245,158,11,0.10)" }}>
                  In the Works · Coming Soon
                </span>
              </div>
              <h3 className="relative font-display text-xl md:text-2xl mb-2" style={{ color: "var(--text)" }}>Streamline stock verification & audits</h3>
              <p className="relative text-sm mb-4" style={{ color: "var(--text-muted)" }}>
                Structured task assignment, field execution, documentation and reporting — with SKU-level scanning and multi-location workflows.
              </p>
              <div className="relative flex flex-wrap gap-2 mb-6">
                <Tag label="Field execution" color="#F59E0B"/>
                <Tag label="Barcode / SKU scan"/>
                <Tag label="Audit reports" color="#22D3EE"/>
              </div>

              {/* Illustrative "3D" stack of boxes + scanner */}
              <div className="relative h-32 md:h-40 rounded-md border overflow-hidden"
                   style={{ borderColor: "var(--border-strong)", background: "var(--surface-2)" }}>
                <div className="absolute inset-0 flex items-end justify-center gap-2 pb-4">
                  {[0, 1, 2, 3, 4].map(i => (
                    <div key={i} className="rounded-sm"
                         style={{
                           width: 36, height: 44 + i * 8,
                           background: `linear-gradient(180deg, rgba(59,130,246,${0.15 + i*0.06}), rgba(59,130,246,0.02))`,
                           border: "1px solid rgba(59,130,246,0.35)",
                           transform: `perspective(400px) rotateX(4deg) translateY(${i%2?-2:0}px)`,
                         }}/>
                  ))}
                </div>
                <div className="absolute top-3 right-3 px-2 py-1 rounded-md border text-[10px] inline-flex items-center gap-1"
                     style={{ borderColor: "rgba(245,158,11,0.35)", color: "#F59E0B", background: "rgba(245,158,11,0.08)" }}>
                  <ScanLine size={10}/> Scan · SKU-1042
                </div>
              </div>
            </div>

            {/* GST Workflows */}
            <div className="card-surface p-6 md:p-8 relative overflow-hidden">
              <div className="absolute inset-0 opacity-40 pointer-events-none grid-bg-fine"/>
              <div className="relative flex items-center justify-between mb-4">
                <Eyebrow><FileCheck size={12}/> GST Workflows</Eyebrow>
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border"
                      style={{ borderColor: "rgba(245,158,11,0.35)", color: "#F59E0B", background: "rgba(245,158,11,0.10)" }}>
                  In the Works · Coming Soon
                </span>
              </div>
              <h3 className="relative font-display text-xl md:text-2xl mb-2" style={{ color: "var(--text)" }}>GST-focused business workflows</h3>
              <p className="relative text-sm mb-4" style={{ color: "var(--text-muted)" }}>
                Task templates and structured deliverables for GST documentation, verification and compliance support — supervised by admin.
              </p>
              <div className="relative flex flex-wrap gap-2 mb-6">
                <Tag label="Document verify" color="#10B981"/>
                <Tag label="Reconciliation"/>
                <Tag label="Filing support" color="#22D3EE"/>
              </div>

              <div className="relative h-32 md:h-40 rounded-md border overflow-hidden"
                   style={{ borderColor: "var(--border-strong)", background: "var(--surface-2)" }}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="absolute rounded-md border"
                           style={{
                             width: 120, height: 78, top: -i * 6, left: -i * 6,
                             background: "linear-gradient(180deg, rgba(16,185,129,0.15), rgba(16,185,129,0.02))",
                             borderColor: "rgba(16,185,129,0.35)",
                             transform: `perspective(500px) rotateX(6deg) rotateY(${-6+i*3}deg)`,
                           }}>
                        <div className="p-2">
                          <div className="h-1.5 w-14 rounded-full" style={{ background: "#10B981", opacity: 0.9 }}/>
                          <div className="mt-1.5 h-1 w-20 rounded-full" style={{ background: "rgba(148,163,184,0.5)" }}/>
                          <div className="mt-1 h-1 w-16 rounded-full" style={{ background: "rgba(148,163,184,0.4)" }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="absolute top-3 right-3 px-2 py-1 rounded-md border text-[10px] inline-flex items-center gap-1"
                     style={{ borderColor: "rgba(16,185,129,0.35)", color: "#10B981", background: "rgba(16,185,129,0.08)" }}>
                  <ShieldCheck size={10}/> Verified
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FUTURE SERVICES ============ */}
      <section className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <SectionTitle
            eyebrow={<><Sparkles size={12}/> Roadmap</>}
            title="More professional services coming to Assisto4U."
          />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {[
              { i: FileCheck,      l: "Accounting" },
              { i: Package,        l: "Inventory ops" },
              { i: ClipboardList,  l: "Field surveys" },
              { i: ShieldCheck,    l: "Compliance" },
              { i: BarChart3,      l: "Data audits" },
              { i: Building2,      l: "Corporate research" },
            ].map(s => (
              <div key={s.l} className="card-surface p-4 flex flex-col items-start gap-3">
                <div className="w-8 h-8 rounded-md flex items-center justify-center"
                     style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)" }}>
                  <s.i size={14} style={{ color: "var(--brand)" }}/>
                </div>
                <div className="text-sm" style={{ color: "var(--text)" }}>{s.l}</div>
                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full border"
                      style={{ borderColor: "var(--border-strong)", color: "var(--text-dim)" }}>Planned</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16 md:pb-24">
        <div className="card-surface p-6 md:p-10 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg-fine opacity-70"/>
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="max-w-2xl">
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl" style={{ color: "var(--text)" }}>Ready to build a verifiable career?</h2>
              <p className="mt-3 text-sm md:text-base" style={{ color: "var(--text-muted)" }}>
                Join thousands of students and companies transacting on Assisto4U every week.
              </p>
            </div>
            <div className="lg:ml-auto flex flex-wrap gap-3">
              <button data-testid="cta-student-btn" onClick={() => navigate("/auth")}
                      className="btn-primary px-5 py-3 rounded-full inline-flex items-center gap-2 text-sm">
                <GraduationCap size={15}/> I'm a student
              </button>
              <button data-testid="cta-company-btn" onClick={() => navigate("/auth")}
                      className="btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2 text-sm">
                <Building2 size={15}/> I'm a company
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10 flex flex-col md:flex-row gap-6 items-center">
          <Logo/>
          <div className="text-xs" style={{ color: "var(--text-dim)" }}>© 2026 Assisto4U · A verified marketplace for professional tasks</div>
          <div className="md:ml-auto flex items-center gap-5 text-xs" style={{ color: "var(--text-muted)" }}>
            <a href="#" className="hover:opacity-100 opacity-80">Privacy</a>
            <a href="#" className="hover:opacity-100 opacity-80">Terms</a>
            <a href="#" className="hover:opacity-100 opacity-80">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
